import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AddNewItemButton from "../../../../components/buttons/AddNewItemButton";
import IconButton from "../../../../components/IconButton";
import TextField from "../../../../components/input-fields/TextField";
import Tooltip from "../../../../components/Tooltip";
import { getFeatureFlag } from "../../../../services-logic/base-logic";
import { formatPhoneNumberUsingFormats } from "../../../../utils/utilPhoneNumbers";
import ContactNameSection from "./accordion-body-components/ContactNameSection";
import DtmfShortcutSection from "./accordion-body-components/DtmfShortcutSection";
import EmailSection from "./accordion-body-components/EmailSection";
import DropdownField from "../../../../components/input-fields/DropdownField";

const defaultContactDestinationFormatters = [
  {
    type: "phone_number",
    label: "Phone number",
  },
  {
    type: "extension",
    label: "User/Extension",
  },
  {
    type: "voicemail",
    label: "Leave a voicemail for User/Extension",
  },
  {
    type: "email",
    label: "Email a message",
    customAttributes: {
      displayMode: "always",
    },
  },
  {
    type: "custom",
    label: "Custom",
  },
];

const ContactsAccordionBody = ({ item, index, onContactUpdated }) => {
  const contactDestinationFormatters = getFeatureFlag(
    "Account.CallHandling.ContactDestinationFormatters",
    null,
    defaultContactDestinationFormatters
  );

  const emailFormatter = contactDestinationFormatters.find((option) => option.type === "email");
  const emailDisplayMode = emailFormatter?.customAttributes?.displayMode;

  return (
    <div className="flex flex-col gap-8 w-full py-2 overflow-visible">
      <ContactNameSection contact={item} index={index} onContactUpdated={onContactUpdated} />
      {emailDisplayMode === "always" && (
        <EmailSection contact={item} index={index} onContactUpdated={onContactUpdated} />
      )}
      <Destinations contact={item} index={index} onContactUpdated={onContactUpdated} />
      <DtmfShortcutSection index={index} item={item} onContactUpdated={onContactUpdated} />
    </div>
  );
};

const Destinations = ({ contact, index, onContactUpdated }) => {
  const { config, contactErrors } = useSelector((state) => state.companyDirectory);
  const destinations = useSelector((state) => state.info.destinations);

  const voicemails = destinations.filter((destination) => destination.type === "voicemail");
  const extensions = destinations.filter((destination) => destination.type === "extension");

  const contactDestinationFormatters = getFeatureFlag(
    "Account.CallHandling.ContactDestinationFormatters",
    null,
    defaultContactDestinationFormatters
  );

  const menuOptions = useMemo(() => {
    const hasTerminatedDestination = contact.destinations.some(
      (dest) => dest.type === "voicemail" || dest.type === "email"
    );

    const leftExtensions = extensions.filter(
      (extension) => !contact.destinations.some((dest) => dest.value === extension.id)
    );

    const leftVoicemails = voicemails.filter(
      (voicemail) => !contact.destinations.some((dest) => dest.value === voicemail.id)
    );

    var filteredContactDestinationFormatters = contactDestinationFormatters.filter(
      (option) =>
        !(option.type === "voicemail" && (hasTerminatedDestination || leftVoicemails.length === 0)) &&
        !(option.type === "email" && hasTerminatedDestination)
    );

    filteredContactDestinationFormatters = filteredContactDestinationFormatters.filter((option) => {
      if (option.type === "extension") {
        return leftExtensions.length > 0;
      }

      if (option.type === "voicemail") {
        return leftVoicemails.length > 0;
      }

      return true;
    });

    return filteredContactDestinationFormatters;
  }, [contactDestinationFormatters, contact.destinations]);

  const maxDestinations = getFeatureFlag("Account.CompanyDirectory.MaxDestinationsNumber", null, 4);

  const options = useMemo(() => {
    const filteredOptions = menuOptions.filter((option) => contactDestinationFormatters.includes(option.type));
    if (filteredOptions.length === 0) {
      return menuOptions;
    }

    return filteredOptions.map((option) => {
      const isExtension = option.type === "extension";
      const isVoicemail = option.type === "voicemail";

      const extensionsOptions = destinations.filter((destination) => destination.type === "extension");
      const voicemailOptions = destinations.filter((destination) => destination.type === "voicemail");

      const hasExtensions = extensionsOptions && extensionsOptions.length > 0;
      const hasVoicemail = voicemailOptions && voicemailOptions.length > 0;

      if (isExtension && hasExtensions) {
        return {
          ...option,
          value: option.type,
          disabled: !hasExtensions,
          message: !hasExtensions ? "No extensions defined" : undefined,
        };
      }

      if (isVoicemail && hasVoicemail) {
        return {
          ...option,
          value: option.type,
          disabled: !hasVoicemail,
          message: !hasVoicemail ? "No voicemail defined" : undefined,
        };
      }

      return option;
    });
  }, [contactDestinationFormatters, menuOptions, destinations, contact.destinations]);

  const onUpdateDestination = (destination, i) => {
    const destinations = contact.destinations.map((d, destIndex) => (destIndex === i ? destination : d));
    onContactUpdated(index, "destinations", sortDestinations(destinations));
  };

  const onAddNewDestination = (type) => {
    const destinations = [...contact.destinations];
    destinations.push({
      type: type,
      value: "",
      transferType: "cold",
    });

    onContactUpdated(index, "destinations", sortDestinations(destinations));
  };

  const onDeleteDestination = (i) => {
    const destinations = contact.destinations.filter((_, index) => index !== i);
    onContactUpdated(index, "destinations", destinations);
  };

  const isValidDestinations = contact.destinations.every((destination, dIndex) => {
    return contact.destinations[dIndex].type === "email" || destination.value.length > 0;
  });

  const sortDestinations = (destinations) => {
    const voicemail = destinations.find((dest) => dest.type === "voicemail");
    const email = destinations.find((dest) => dest.type === "email");
    if (voicemail) {
      destinations.splice(destinations.indexOf(voicemail), 1);
      destinations.push(voicemail);
    } else if (email) {
      destinations.splice(destinations.indexOf(email), 1);
      destinations.push(email);
    }
    return destinations;
  };

  const isReachMaxDestinations = contact.destinationslength >= maxDestinations;

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex flex-col gap-6">
        {contact.destinations.map((destination, destinationIndex) => {
          return (
            <DestinationRow
              key={`destination-${destinationIndex}`}
              contactIndex={index}
              contact={contact}
              title={contactDestinationFormatters.find((option) => option.type === destination.type)?.label}
              destination={destination}
              destinationIndex={destinationIndex}
              onUpdateDestination={onUpdateDestination}
              onDeleteDestination={onDeleteDestination}
              destinationError={
                contactErrors[index]?.destinations && contactErrors[index]?.destinations[destinationIndex]
              }
            />
          );
        })}
      </div>
      <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center">
        <AddNewItemMenu
          title="+ Add destination"
          menuItems={options}
          onAddNewItem={onAddNewDestination}
          isValid={isValidDestinations}
          disabled={!isValidDestinations || isReachMaxDestinations}
        />
        {isReachMaxDestinations && (
          <span className="text-[10px] md:text-[12px]">
            You have reached the maximum number of destinations allowed for this contact
          </span>
        )}
      </div>

      {contactErrors[index]?.noDestinations && (
        <div className="flex items-start md:items-center gap-2">
          <img src="/assets/icons/common/error-badge.svg" alt="error" className="size-4" />
          <span className="text-red-500 font-semibold text-[12px] md:text-[14px]">
            {contactErrors[index]?.noDestinations}
          </span>
        </div>
      )}
    </div>
  );
};

const AddNewItemMenu = ({ title, menuItems, onAddNewItem, isValid, disabled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const onMenuClicked = () => {
    if (menuItems.length === 1) {
      onMenuItemSelected(menuItems[0].type);
      return;
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const onMenuItemSelected = (type) => {
    onAddNewItem(type);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".add-new-item-menu")) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const longestLabel = menuItems?.reduce(
    (longest, item) => (item?.label?.length > longest?.length ? item?.label : longest),
    ""
  );

  return (
    <div className="relative inline-block">
      <AddNewItemButton
        title={title}
        onClick={onMenuClicked}
        disabled={disabled}
        onDisabledTooltip={!isValid ? "Finish filling out incomplete items before adding a new one" : ""}
      />
      {isMenuOpen && (
        <div
          className="min-w-48 z-30 add-new-item-menu absolute flex flex-col bottom-full border  bg-white rounded-md shadow-md text-[12px] md:text-[14px]"
          style={{ width: longestLabel.length * 8 + "px" }}
        >
          {menuItems.map((item, index) => {
            return (
              <Tooltip key={`menu-option-${index}`} direction={"right"} text={item.message} width="w-[200px]">
                <button
                  className="w-full px-4 py-2 text-start text-ov-green font-semibold border-b rounded-md hover:bg-ov-green hover:text-white disabled:opacity-50 disabled:pointer-events-none disabled:text-slate-700"
                  onClick={() => onMenuItemSelected(item.type)}
                  disabled={item.disabled}
                >
                  + {item.label}
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DestinationRow = ({
  contact,
  contactIndex,
  title,
  destination,
  destinationError,
  destinationIndex,
  onUpdateDestination,
  onDeleteDestination,
}) => {
  const destinations = useSelector((state) => state.info.destinations);
  const voicemails = destinations.filter((destination) => destination.type === "voicemail");
  const extensions = destinations.filter((destination) => destination.type === "extension");

  const filteredUnusedExtensions = extensions.filter(
    (extension) =>
      !contact.destinations.some(
        (dest, index) => dest.type === "extension" && dest.value === extension.id && index !== destinationIndex
      )
  );

  const isDuplicateExtension = contact.destinations.some(
    (dest, index) => dest.type === "extension" && dest.value === destination.value && index !== destinationIndex
  );

  const contactDestinationFormatters = getFeatureFlag(
    "Account.CallHandling.ContactDestinationFormatters",
    null,
    defaultContactDestinationFormatters
  );

  const isScreeningCheckboxEnabled = getFeatureFlag("Account.CallHandling.ScreeningCheckbox", null, false);

  const destinationFormatter = contactDestinationFormatters.find((option) => option.type === destination.type);
  const emailDisplayMode = destinationFormatter?.customAttributes?.displayMode;
  const typeRegex = {
    phone_number: {
      regex: /^[0-9+ -()]*$/,
      message: "Numbers only",
    },
    extension: {
      regex: null,
      message: "Invalid extension",
    },
    custom: {
      regex: null, // Or provide a specific regex if needed
      message: "Invalid custom",
    },
    email: {
      regex: null,
      message: "Invalid email",
    },
  };

  const onUpdateDestinationKey = (key, value) => {
    onUpdateDestination({ ...destination, [key]: value }, destinationIndex);
  };

  const icons = {
    phone_number: "/assets/icons/destinations/phone_number.svg",
    extension: "/assets/icons/destinations/extension.svg",
    voicemail: "/assets/icons/destinations/voicemail.svg",
    email: "/assets/icons/destinations/email.svg",
    custom: "/assets/icons/destinations/custom.svg",
  };

  if (destination.type === "email" && emailDisplayMode !== "by-user") {
    return null;
  }

  return (
    <div className={`flex gap-2 w-full overflow-visible sm:items-center`}>
      {destination.type === "extension" && (
        <DropdownField
          title={title}
          // defaultOption={{ value: "", option: title }}
          options={filteredUnusedExtensions.map((extension) => ({ value: extension.id, option: `${extension.label}` }))}
          selectedOption={destination.value}
          onChange={(extension) => {
            onUpdateDestinationKey("value", extension);
          }}
          openDirection={"down"}
          errorMessage={isDuplicateExtension ? "Duplicate extension" : destinationError}
          prefixIcon={icons[destination.type]}
        />
      )}
      {destination.type === "voicemail" && (
        <DropdownField
          title={title}
          // defaultOption={{ value: "", option: title }}
          options={voicemails.map((voicemail) => ({ value: voicemail.id, option: voicemail.label }))}
          selectedOption={destination.value}
          onChange={(voicemail) => {
            onUpdateDestinationKey("value", voicemail);
          }}
          openDirection={"down"}
          errorMessage={destinationError}
          prefixIcon={icons[destination.type]}
        />
      )}
      {destination.type !== "extension" && destination.type !== "voicemail" && (
        <TextField
          title={title}
          value={
            destination.type === "phone_number" ? formatPhoneNumberUsingFormats(destination.value) : destination.value
          }
          placeholder={title}
          onChange={(value) => onUpdateDestinationKey("value", value)}
          required
          errorMessage={destinationError}
          regexMessage={typeRegex[destination.type].message}
          inputRegex={typeRegex[destination.type].regex}
          prefixIcon={icons[destination.type]}
          maxCharacters={destination.type === "extension" ? 4 : null}
        />
      )}
      <div className="flex items-center gap-2">
        {isScreeningCheckboxEnabled && destination.type !== "email" && destination.type !== "voicemail" && (
          <div className="flex items-center gap-2">
            <ScreeningCheckbox
              destination={destination}
              contactIndex={contactIndex}
              destinationIndex={destinationIndex}
              onUpdateDestinationKey={onUpdateDestinationKey}
            />
            <Tooltip
              direction={"left"}
              text="Check 'Screening' if you would like the caller's name to be announced to the recipient. The recipient can then decide whether to accept or decline the call. Uncheck 'Screening' if you prefer to send calls directly to their destination without any screening."
            />
          </div>
        )}
        <IconButton
          iconSize="size-5"
          icon={"/assets/icons/actions/delete.svg"}
          hoverIcon={"/assets/icons/actions/delete-hover.svg"}
          onClick={() => onDeleteDestination(destinationIndex)}
        />
      </div>
    </div>
  );
};

const ScreeningCheckbox = ({ destination, contactIndex, destinationIndex, onUpdateDestinationKey }) => {
  return (
    <div className="flex flex-col sm:flex-row gap-1 items-center">
      <input
        id={`screeningCheckbox-${contactIndex}-${destinationIndex}`} // Include the parent index for uniqueness
        className="accent-primary"
        type="checkbox"
        checked={destination.transferType === "warm"}
        onChange={() => onUpdateDestinationKey("transferType", destination.transferType === "warm" ? "cold" : "warm")}
      />
      <label
        htmlFor={`screeningCheckbox-${contactIndex}-${destinationIndex}`} // Match the label to the unique id
        className="text-center text-[10px] md:text-[12px]"
      >
        Screening
      </label>
    </div>
  );
};

export default ContactsAccordionBody;
